import { useToast } from 'components/ToastManager';
import React, { useMemo, useState } from 'react';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Tabs } from '@fragment/ui/src/components/Tabs/Tabs';
import { Toast } from '@fragment/ui/src/components/Toast';

import { PaymentsModal } from './PaymentsModal';

type PayoutMode = 'single' | 'bulk';

type Invoice = {
  id: string;
  amount: string;
  currency: string;
  status: string;
  createdAt: string;
};

type PayoutModalProps = {
  onClose: VoidFunction;
  invoices: Invoice[];
  userName: string;
};

const SingleInvoicePane = ({
  invoices,
  selectedInvoiceId,
  onSelectInvoice,
}: {
  invoices: Invoice[];
  selectedInvoiceId: string | null;
  onSelectInvoice: (id: string) => void;
}) => {
  if (invoices.length === 0) {
    return (
      <div className="flex items-center justify-center py-f8 text-main-500">
        No active invoices available
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-f3 max-h-[400px] overflow-y-auto">
      <div className="text-main-500 text-small">
        Select an invoice to pay out:
      </div>
      <div className="flex flex-col gap-y-f2">
        {invoices.map((invoice) => (
          <label
            aria-label="invoice-option"
            key={invoice.id}
            className="flex items-start gap-x-f2 p-f2 border border-main-300 rounded hover:bg-main-100 cursor-pointer"
          >
            <input
              type="radio"
              name="invoice-selection"
              checked={selectedInvoiceId === invoice.id}
              onChange={() => onSelectInvoice(invoice.id)}
              className="mt-1"
            />
            <div className="flex flex-col gap-y-f1 flex-1">
              <div className="text-main font-medium">Invoice {invoice.id}</div>
              <div className="text-main-500 text-small">
                Amount: {invoice.amount} {invoice.currency} • Status:{' '}
                {invoice.status}
              </div>
              <div className="text-main-500 text-small">
                Created: {new Date(invoice.createdAt).toLocaleDateString()}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

const BulkPayoutPane = ({
  invoices,
  selectedInvoiceIds,
  onToggleInvoice,
}: {
  invoices: Invoice[];
  selectedInvoiceIds: Set<string>;
  onToggleInvoice: (id: string) => void;
}) => {
  const totalAmount = useMemo(() => {
    const selected = invoices.filter((inv) => selectedInvoiceIds.has(inv.id));
    const sum = selected.reduce((acc, inv) => acc + parseFloat(inv.amount), 0);
    const currency = selected.length > 0 ? selected[0].currency : '';
    return { sum: sum.toFixed(2), currency };
  }, [invoices, selectedInvoiceIds]);

  if (invoices.length === 0) {
    return (
      <div className="flex items-center justify-center py-f8 text-main-500">
        No active invoices available
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-f3">
      <div className="text-main-500 text-small">
        Select multiple invoices to pay out in bulk:
      </div>
      <div className="max-h-[350px] overflow-y-auto flex flex-col gap-y-f2">
        {invoices.map((invoice) => (
          <label
            aria-label="select-invoice"
            key={invoice.id}
            className="flex items-start gap-x-f2 p-f2 border border-main-300 rounded hover:bg-main-100 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedInvoiceIds.has(invoice.id)}
              onChange={() => onToggleInvoice(invoice.id)}
              className="mt-1"
            />
            <div className="flex flex-col gap-y-f1 flex-1">
              <div className="text-main font-medium">Invoice {invoice.id}</div>
              <div className="text-main-500 text-small">
                Amount: {invoice.amount} {invoice.currency} • Status:{' '}
                {invoice.status}
              </div>
              <div className="text-main-500 text-small">
                Created: {new Date(invoice.createdAt).toLocaleDateString()}
              </div>
            </div>
          </label>
        ))}
      </div>
      {selectedInvoiceIds.size > 0 && (
        <div className="pt-f2 border-t border-main-300">
          <div className="text-main font-medium">
            Total: {totalAmount.sum} {totalAmount.currency}
          </div>
          <div className="text-main-500 text-small">
            {selectedInvoiceIds.size} invoice
            {selectedInvoiceIds.size !== 1 ? 's' : ''} selected
          </div>
        </div>
      )}
    </div>
  );
};

export const PayoutModal = ({
  onClose,
  invoices,
  userName,
}: PayoutModalProps) => {
  const { showToast } = useToast();
  const [mode, setMode] = useState<PayoutMode>('single');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(
    new Set()
  );

  // Generate random amounts for each invoice (400-600 USD)
  const invoiceAmounts = useMemo(() => {
    const amounts = new Map<string, number>();
    invoices.forEach((invoice) => {
      const randomAmount = Math.floor(Math.random() * (600 - 400 + 1)) + 400;
      amounts.set(invoice.id, randomAmount);
    });
    return amounts;
  }, [invoices]);

  // Create invoices with random amounts
  const invoicesWithRandomAmounts = useMemo(
    () =>
      invoices.map((invoice) => ({
        ...invoice,
        amount: invoiceAmounts.get(invoice.id)?.toFixed(2) || '0.00',
        currency: 'USD',
      })),
    [invoices, invoiceAmounts]
  );

  const tabOptions = useMemo(
    () => [
      { label: 'Single Invoice', value: 'single' as const },
      { label: 'Bulk Payout', value: 'bulk' as const },
    ],
    []
  );

  const handleToggleInvoice = (id: string) => {
    setSelectedInvoiceIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSubmit = () => {
    showToast(<Toast type="error" message="Payout support coming soon!" />);
    if (mode === 'single') {
      // TODO: Handle single invoice payout
      console.log('Processing payout for invoice:', selectedInvoiceId);
    } else {
      // TODO: Handle bulk payout
      console.log('Processing bulk payout for invoices:', [
        ...selectedInvoiceIds,
      ]);
    }
    onClose();
  };

  const canSubmit =
    mode === 'single'
      ? selectedInvoiceId !== null
      : selectedInvoiceIds.size > 0;

  return (
    <PaymentsModal
      data-testid="payout-modal"
      onClose={onClose}
      width="md"
      header={<div className="text-main">Payout to {userName}</div>}
      body={
        <div className="flex flex-col w-full gap-y-f4">
          <Tabs
            options={tabOptions}
            selectedValue={mode}
            onClick={(value: PayoutMode) => {
              setMode(value);
              setSelectedInvoiceId(null);
              setSelectedInvoiceIds(new Set());
            }}
          />
          {mode === 'single' ? (
            <SingleInvoicePane
              invoices={invoicesWithRandomAmounts}
              selectedInvoiceId={selectedInvoiceId}
              onSelectInvoice={setSelectedInvoiceId}
            />
          ) : (
            <BulkPayoutPane
              invoices={invoicesWithRandomAmounts}
              selectedInvoiceIds={selectedInvoiceIds}
              onToggleInvoice={handleToggleInvoice}
            />
          )}
        </div>
      }
      footer={
        <div className="flex flex-row justify-between w-full">
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            submitButtonLabel="Process Payout"
            onCancel={onClose}
            onSubmit={handleSubmit}
            disabled={!canSubmit}
          />
        </div>
      }
    />
  );
};
